// DYCS 공유 타입 — console(client) · widget(client) · functions(admin SDK) 공용.
// ⚠️ Firebase SDK 타입에 의존하지 않는다(client/admin 양쪽에서 import). 타임스탬프는 제네릭.
//
// Firestore Timestamp 는 client(firebase/firestore)와 admin(firebase-admin/firestore)에서
// 서로 다른 클래스 → 공유 타입에서는 구조적 호환 인터페이스로 추상화.
import type { RouteScheduleEntry } from './routeSchedule';

/** Firestore Timestamp 의 구조적 최소 형태(client·admin 공통). */
export interface TimestampLike {
  toMillis(): number;
  toDate(): Date;
}

/** 저장된 첨부 파일 메타(Storage 경로 기반). */
export interface StoredFile {
  path: string;
  name: string;
  size?: number;
  contentType?: string;
  uploadedAt?: TimestampLike;
  uploadedBy?: string;
}

// ────────────────────────────────────────────────────────────
// tenants — 거래처 마스터
// ────────────────────────────────────────────────────────────

/** 회신 채널(거래처별 활성화). */
export type ReplyChannel = 'alimtalk' | 'email';

export interface Tenant {
  /** docId 와 동일(tenantId). */
  id: string;
  /** 내부 식별명(예: 'kolon'). */
  name: string;
  /** 위젯·콘솔 표시명(예: 'KOLON'). */
  displayName: string;
  /** 브랜드 컬러(위젯 액센트·콘솔 거래처 칩). hex. */
  brandColor?: string;
  /** 활성 여부 — 위젯은 active 거래처만 노출. */
  active: boolean;
  /** 챗봇 FAQ 답변 사용 여부. */
  faqEnabled: boolean;
  /** 활성화된 회신 채널. */
  replyChannels: ReplyChannel[];
  /** 위젯 임베드 식별 토큰(남용 방지). */
  embedToken: string;
  /** 탑승노선 목록(분실물/문의 드롭다운). 시간표(routeSchedule)를 저장하면 그 노선명으로 함께 갱신된다. */
  routes: string[];
  /**
   * 노선·출발시간표(2026-10-13 게시판 YCAmtzH2). 있으면 위젯 **분실물** 폼이 노선·출발 시간을 이 목록에서
   *   **필수 선택**으로 받는다(「기타 (직접 입력)」 없음). times 는 표시값 그대로('출근 07:00'·'퇴근 17:45').
   *   🔴 비거나 없으면 위젯은 예전과 완전히 같다(새 게이트 기본값 = 현상 유지). 공개 정보라 tenants(공개 read)에 둔다.
   *   파서·판정 정본 `routeSchedule.ts`.
   */
  routeSchedule?: RouteScheduleEntry[];
  /**
   * 자동 시간표(2026-10-14 게시판 MxnsrbK8) — `syncCcDriversDaily`(06:20)가 Buslink Ops 기사 명부(cc_drivers)의
   *   노선·출근 시각으로 매일 채운다(Function 전용 write · 값이 바뀐 거래처만 · 기사 0이면 필드 제거).
   *   🔴 노선·`출근 HH:mm` 만 — 기사 이름·전화·차량 금지(tenants 는 위젯 공개 read).
   *   손입력 routeSchedule 에 시간이 하나라도 있으면 그쪽이 이긴다 — 판정 정본 `effectiveRouteSchedule`.
   */
  routeScheduleAuto?: RouteScheduleEntry[];
  /**
   * 접수 전 **휴대폰 인증번호 확인**을 요구할지(기본 false=미요구).
   *   🔴 공개 홈페이지처럼 **불특정 다수**가 들어오는 창구만 켠다(2026-08-26 way — 홈페이지
   *      고객민원은 아무 번호나 넣는 악성 접수가 많다). 거래처 셔틀 이용자용 위젯·buslink
   *      승객앱 임베드까지 켜면 이미 신원이 확인된 사람에게 한 단계를 더 물리는 셈이라
   *      접수 이탈만 늘어난다.
   *   ⚠️ 카카오 채널 접수(kakaoSkill)는 카카오 계정 자체가 신원이라 이 플래그와 무관하게 면제.
   */
  requirePhoneVerify?: boolean;
  /**
   * 고객사 열람 포털에서 **고객 연락처(전화·이메일)를 가리지 않고** 보여줄지(기본 false=가림).
   *
   * 🔴 기본이 «가림»인 이유: 총무팀이 보는 건 자기 회사 **직원들의** 문의다. 누가 무엇을 물었는지는
   *    업무상 필요하지만 전화번호까지는 다른 문제다(2026-09-01 way 승인). 거래처가 요구하면 켠다 —
   *    **켜는 건 결정이고, 안 켜는 건 기본값**이어야 나중에 "왜 다 보였냐"는 질문에 답할 수 있다.
   * ⚠️ 이 값은 **화면 표기만** 바꾼다. 티켓 문서 자체는 rules 상 통째로 읽히므로 진짜 차단이 필요하면
   *    Function 경유 read 로 좁혀야 한다 — 지금은 «같은 회사 사람이 본다»는 전제 위의 예의 장치다.
   */
  clientPortalShowContact?: boolean;
  /**
   * 신규 티켓 자동 배정 상담원 이메일(비우면 미배정 — 기존 동작).
   *   홈페이지 민원처럼 "담당은 기본적으로 CS 담당자"인 창구에서, 배정 전이라 아무도 자기 일로
   *   여기지 않는 공백을 없앤다. 상담원이 나중에 바꿔도 되는 **초기값**일 뿐이다.
   */
  defaultAssignee?: string;
  /**
   * 신규 접수를 **회사 전체**에 공지할지(구글챗 전사 스페이스 카드 + all@ 메일, 기본 false).
   *   🔴 홈페이지처럼 **불특정 다수의 대외 민원**이 들어오는 창구만 켠다(2026-08-26 way — 구
   *      DYPORTAL 이 고객사이렌 폼으로 하던 것과 동일). 거래처 셔틀 문의까지 켜면 전 직원
   *      스페이스가 firehose 가 되고, 어느 고객사에서 무슨 문의가 왔는지가 전사에 노출된다.
   *   ⚠️ 전사 발송분엔 **고객명·연락처를 싣지 않는다**(companyBroadcast.ts) — 수신자가 특정되지
   *      않기 때문. 담당자용 상세는 `alerts`(개인 알림톡·메일·챗DM)가 따로 보낸다.
   */
  broadcastCompany?: boolean;
  /**
   * 분실물 접수 폼에 **«이용 고객사»** 칸을 띄울지(기본 false=안 띄움).
   *
   * 🔴 기본이 «안 띄움»인 이유: 거래처 전용 링크(`?tenant=<id>`)는 **어느 회사인지가 이미 확정**이다
   *    — 폼 헤더에도 그 이름이 찍혀 있고 티켓에도 `tenantId` 로 남는다. 실측(2026-09-08 way 지적):
   *    위젯 분실물 29건 중 이 칸을 채운 23건 가운데 **20건이 거래처명을 그대로 옮겨 적은 것**이었고,
   *    1건은 노선 칸이 없던 탓에 여기에 노선을 적었다. 고객에게 이미 아는 것을 한 번 더 묻던 칸이다.
   * ✅ 켜야 하는 곳은 둘뿐: ① 홈페이지처럼 **누가 들어올지 모르는 창구**(`requirePhoneVerify` 가
   *    켜져 있으면 이 값과 무관하게 자동으로 뜨고 **필수**다) ② **한 거래처에 여러 회사가 묶인 곳**
   *    — `innen`(이노엔소만사)이 실제로 그렇다(실측 «HK이노엔»·«소만사» 2건이 유일한 유효 입력).
   */
  askCompanyName?: boolean;
  /**
   * 접수 폼에 **거래처 전용 식별번호 칸**을 띄운다. 이 값이 곧 그 칸의 라벨이고, 비어 있으면
   * 칸 자체가 없다(예: `sev`='사번', 대학 거래처라면 '학번').
   *
   * 🔴 왜 거래처별인가(2026-09-09 way): 신촌세브란스는 **셔틀 예약 시스템 비밀번호 재설정**
   *    문의가 대다수인데, 사번 없이는 상담원이 계정을 특정할 수 없어 매번 되묻는 한 바퀴가 돈다
   *    (실측: 같은 고객이 18:01 에 접수 → 사번을 안 적어 20:21 에 사번을 붙여 **재문의**).
   *    다른 거래처엔 사번으로 여는 계정 자체가 없어 켜면 답할 수 없는 질문이 된다.
   * ⚠️ 라벨 문자열이 스위치다 — 별도 on/off 불리언을 두면 «켰는데 라벨이 비어 이름 없는 칸»
   *    이라는 상태가 생긴다.
   */
  employeeNoLabel?: string;
  /**
   * 그 칸을 **필수**로 할지(기본 false=선택).
   *   🔴 기본이 «선택»인 이유: 같은 창구로 분실물·노선 문의도 들어온다. 사번을 모르는(혹은
   *      필요 없는) 문의까지 막으면 접수 자체가 끊긴다 — 되묻는 것보다 나쁘다.
   */
  employeeNoRequired?: boolean;
  /**
   * 사업장 목록(2026-10-15 게시판 sRX4v2c2 · CS 강유정). 예: `db`(DB하이텍) = ['부천','상우'].
   * 비어 있으면 칸 자체가 없다(다른 거래처 현상 유지). 값이 있으면 위젯 문의·분실물 폼이 「사업장」을
   * **필수로** 고르게 하고, 티켓 `site` 에 고른 값이 남아 화면·알림이 `DB하이텍(부천)` 으로 읽힌다.
   * 🔴 거래처를 둘로 쪼개지 않는 이유: `?tenant=db` 위젯 링크가 이미 배포돼 외부 소비자가 있다(URL 계약).
   * 🔴 노선 이름으로 추정하지 않는 이유: 같은 노선 이름(사당·분당)이 두 사업장에 다 있다 — 고객이 고른다.
   */
  sites?: string[];
  /**
   * 「칭찬 릴레이」 접수 창구(2026-10-01 way · 온세미 구글폼 이관). 켜면 위젯 선택 화면에 세 번째
   * 버튼이 붙고 `?type=praise` 고정 링크가 열린다. 폼 = 사번·노선(필수)·칭찬 기사명(선택)·칭찬 내용.
   * 🔴 이름·연락처를 받지 않는다(원 폼 그대로) — 회신 대상이 없는 접수라 알림톡·메일은 안 나간다.
   */
  praiseRelay?: boolean;
  /** 사내 알림 수신자 설정(신규 티켓 시 담당자/팀장 알림). */
  alerts?: TenantAlertConfig;
  createdAt?: TimestampLike;
  updatedAt?: TimestampLike;
}

// ────────────────────────────────────────────────────────────
// 사내 알림(staff alert) — 신규 티켓 접수 시 담당자/팀장에게 통지
//   ⚠️ 고객 회신(notify.ts·알림톡/이메일)과 별개. 수신자는 전부 동영관광 내부 직원.
//   설계: 실시간 개인 타깃(알림톡 폰 + 이메일 백업) + 구글챗은 시간당 다이제스트 1장(강등).
// ────────────────────────────────────────────────────────────

/** 알림 수신자 역할(거래처별 3종). */
export type StaffAlertRole = 'cs' | 'account' | 'lead';

/** 사내 알림 수신자 1인(전부 내부 직원). */
export interface StaffAlertRecipient {
  /** 표시 이름(선택). */
  name?: string;
  /** 이메일(백업 알림 — 없으면 이메일 skip). */
  email?: string;
  /** 휴대폰(알림톡 폰 푸시 — 숫자만, 없으면 알림톡 skip). */
  phone?: string;
  /** 구글챗 숫자 userId(`123…` — 다이제스트 @멘션용. 없으면 멘션 생략). */
  chatUserId?: string;
}

/** 거래처별 사내 알림 설정(tenants 문서에 임베드). */
export interface TenantAlertConfig {
  /** 신규 티켓 시 사내 알림 사용 여부(기본 false=미사용). */
  enabled?: boolean;
  /** CS 담당자. */
  cs?: StaffAlertRecipient[];
  /** 거래처 담당자(이 거래처를 맡은 내부 직원). */
  account?: StaffAlertRecipient[];
  /** 팀장. */
  lead?: StaffAlertRecipient[];
}

// ────────────────────────────────────────────────────────────
// 영업담당자 연동 — DYOPS(dy-ops) '외근 거래처 조직도' 미러 + 거래처별 연결
//   정본은 **DYOPS `workoutside_clients`**(dy-ops 프로젝트). dycs 는 읽기 미러만 갖는다.
//   🔴 담당자 이름·이메일은 사내 개인정보다. `tenants` 는 위젯이 **공개 read** 하므로
//      절대 tenants 에 넣지 않는다 — 상담원만 읽는 `sales_clients`·`tenant_sales` 에 둔다.
// ────────────────────────────────────────────────────────────

/** DYOPS 조직도 거래처 1건(미러). docId = DYOPS 쪽 docId(=정규화된 이름). */
export interface SalesClient {
  /** docId = DYOPS 거래처명(정규화). 조인 키가 곧 이름인 저쪽 규약을 그대로 받는다. */
  id: string;
  /** 거래처명(DYOPS 표기 그대로 — dycs displayName 과 다를 수 있다). */
  name: string;
  /** 순수 팀명(영업1팀·영업2팀·영업지원팀·영업본부). */
  team: string;
  /** 파트('1'·'2'). 팀장은 없음. */
  part?: string;
  /** 담당자 이름(DYOPS users.displayName 조인 결과). */
  managerName: string;
  /** 담당자 사내 이메일. 알림 수신자 해석의 키. */
  managerEmail?: string;
  /** DYOPS 쪽 active. false 면 선택 후보에서 감춘다(기존 연결은 유지·경고). */
  active: boolean;
  /** 마지막 미러 시각. */
  syncedAt?: TimestampLike;
}

/** 거래처에 연결된 영업담당자 1명(연결 시점 denorm — 표시·알림 수신자 해석용). */
export interface SalesOwner {
  /** 연결된 DYOPS 거래처 docId. */
  clientId: string;
  /** 연결된 DYOPS 거래처명(표시용 — 어느 항목으로 이어졌는지 보이게). */
  clientName: string;
  managerName: string;
  managerEmail?: string;
  /**
   * 영업담당자 휴대폰(숫자만 · 2026-09-18). 🔴 **정본은 `sales_contacts/{email}`** 이고 여기는 denorm 이다 —
   * 매일 `salesSync` 가 owners 를 통째로 다시 만들므로, 여기에만 적으면 하루 만에 사라진다.
   */
  managerPhone?: string;
  team: string;
  part?: string;
}

/**
 * `sales_contacts/{emailLower}` — 영업담당자 연락처 정본(조직도에 없는 값·2026-09-18).
 * DYOPS 조직도 미러에는 휴대폰이 없어(`sales_clients` 실측) 사내 「법인폰 관리 현황」 시트에서 채운다.
 * read=isAgent · write=Function/스크립트(admin) 전용.
 */
export interface SalesContact {
  /** docId = 이메일 소문자. */
  id: string;
  name?: string;
  /** 숫자만(01012345678). */
  phone?: string;
  /** 출처 표기 — 어디서 온 값인지 모르면 누구도 못 고친다. */
  source?: string;
  updatedAt?: TimestampLike;
  updatedBy?: string;
}

/**
 * 거래처 현장 담당자 1명(소장 등 · 2026-09-21).
 * 🔴 조직도(DYOPS)에 **없는 축**이다 — 영업담당자는 거래처를 맡은 내부 영업이고, 소장은
 *    그 현장에 상주하는 사람이라 동기화로 채울 수 없다. 사람이 콘솔에서 적는 값이고
 *    `salesSync` 가 손대지 않는다(`{owners, syncedAt}` merge 라 이 필드는 살아남는다).
 */
export interface SiteContact {
  /** 이름(유일한 필수값 — 이름 없는 번호는 누구에게 거는지 모른다). */
  name: string;
  /** 역할 표기('소장'·'관리소장'·'현장반장' 등). 비면 화면에서 생략. */
  role?: string;
  /** 연락처(숫자만 저장 · 표시할 때 하이픈). */
  phone?: string;
  /**
   * 연락 가능한 시간('평일 06:00~15:00' 등 · 자유 문자열).
   * 🔴 코드가 해석하지 않는다 — 근무 형태가 현장마다 달라(격일·교대) 구조화하면
   *    못 적는 현장이 생긴다. 상담원이 읽고 판단할 한 줄이면 충분하다.
   */
  hours?: string;
  /** 비고(내선·대체 연락 등). */
  note?: string;
}

/**
 * `tenant_sales/{tenantId}` — 거래처 ↔ DYOPS 조직도 연결(상담원 전용 read).
 *   🔴 1:다 를 허용한다 — 실측상 한 거래처가 조직도 2건에 걸린다
 *      (이노엔소만사 = HK이노엔 + 소만사 · DB하이텍 = DB하이텍 + DB하이텍(상우)).
 *   이름 자동 매칭은 채택하지 않는다(1:다·다:1·무대응이 모두 실재 — 사람이 고른다).
 */
export interface TenantSales {
  /** docId = tenantId. */
  id: string;
  /** 연결된 DYOPS 거래처 docId 목록(정본). */
  clientIds: string[];
  /** 위 연결의 denorm 스냅샷 — 동기화가 갱신한다(담당자 교체 자동 반영). */
  owners: SalesOwner[];
  /**
   * 현장 담당자(소장 등) — 손입력 정본. 🔴 `owners` 와 달리 동기화 대상이 **아니다**.
   * 🔴 여기 두는 이유 = `tenants` 는 위젯 공개 read 라 사내 연락처를 넣을 수 없고,
   *    이 컬렉션은 이미 read=isAgent 다(rules 변경 0 · 인박스가 이미 이 문서를 읽는다).
   */
  siteContacts?: SiteContact[];
  /** 마지막 동기화 시각(미러 갱신 때 owners 재계산). */
  syncedAt?: TimestampLike;
  updatedAt?: TimestampLike;
  updatedBy?: string;
}

// ────────────────────────────────────────────────────────────
// tickets — 문의·분실물 통합 티켓 (단일 컬렉션, type 으로 분기)
// ────────────────────────────────────────────────────────────

export type TicketType = 'inquiry' | 'lost_item';
/**
 * 티켓 상태.
 *   'phone_resolved' = **고객과 통화 후 처리완료**(2026-10-15 게시판 KuvKmNW3 강유정). 답변완료와 같은
 *   무게의 종결 상태지만 **서면 답변이 나간 게 아니다** — 그래서 고객 알림톡·메일·구글챗 답변 카드는
 *   발화하지 않는다(notify·salesChat 의 `status==='answered'` 게이트가 그대로 막는다 · 확장 금지).
 */
export type TicketStatus = 'received' | 'in_progress' | 'answered' | 'phone_resolved' | 'closed';
export type TicketSource = 'web' | 'chatbot' | 'migrated';
export type TicketChannel = 'web' | 'kakao';

/** 문의(inquiry) 분류. */
export type InquiryCategory = '노선' | '시간' | '서비스' | '칭찬' | '기타';

/** 분실물 진행 상태. */
export type LostItemStatus = 'storing' | 'returned' | 'not_found';

export interface Ticket {
  id: string;
  tenantId: string;
  type: TicketType;
  status: TicketStatus;

  // 고객 정보
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  /** 탑승노선. */
  route?: string;
  content: string;

  // 메타
  source: TicketSource;
  channel: TicketChannel;
  /** 개인정보 수집·이용 동의. */
  privacyConsent: boolean;
  /**
   * 접수 시 **휴대폰 인증번호 확인을 통과**했는지(tenants.requirePhoneVerify 창구).
   *   없음/false = 인증을 안 거친 접수(기존 티켓·거래처 위젯·카카오). 상담원이 번호의
   *   신뢰도를 눈으로 구분할 수 있어야 하므로 티켓에 사실로 남긴다.
   */
  phoneVerified?: boolean;
  /**
   * 고객사(소속 회사·기관). 홈페이지처럼 **어느 거래처 셔틀인지 티켓만으론 모르는** 창구에서
   * 고객이 직접 적는다 — 분실물은 이 값이 없으면 어느 차량·어느 보관함을 뒤질지가 안 나온다
   * (2026-08-26 way). 거래처 전용 위젯은 tenantId 가 이미 답이라 요구하지 않는다.
   */
  companyName?: string;
  /**
   * 거래처 전용 식별번호(사번·학번 등). `tenants.employeeNoLabel` 이 켜진 창구에서만 채워진다.
   * 🔴 **표시 라벨은 여기 없다** — 거래처 설정(`employeeNoLabel`)이 정본이고 화면이 그걸 읽는다.
   */
  employeeNo?: string;
  /**
   * 사업장(`tenants.sites` 가 있는 거래처에서 고객이 고른 값 · 2026-10-15 sRX4v2c2).
   * 표시는 `tenantLabel(거래처명, site)` 하나로 — 'DB하이텍(부천)'.
   */
  site?: string;
  /** 「칭찬 릴레이」 창구 접수(tenants.praiseRelay) — 이름·연락처 없이 사번으로만 들어온다. */
  praiseRelay?: boolean;
  /** 칭찬 대상 기사명(칭찬 릴레이 · 선택 입력). */
  praiseDriverName?: string;
  /** 챗봇이 즉답했는지. */
  botHandled?: boolean;
  /** 담당 상담원 이메일. */
  assignedTo?: string;
  /** 마이그레이션 원본번호. */
  legacyNo?: string;
  /** 테스트 데이터(마이그레이션 시 태깅) — 기본 인박스에서 숨김. */
  isTest?: boolean;
  /** 마이그레이션 원본 타임스탬프 텍스트(파싱 전 원형 — 상세 표시). */
  legacyTimestampText?: string;

  // ── 회신속도(첫 응대) ─────────────────────────────────────
  //   🔴 정본은 이 두 필드다. 회신속도를 매번 messages 서브컬렉션에서 되계산하지 않는다
  //      (티켓 N건 × 스레드 1쿼리 = 통계 화면이 수천 read 를 쓴다).
  //   🔴 마이그레이션 과거분엔 **없다**(원본에 신뢰할 답변 시각이 없음 · updatedAt=이관 시각).
  //      없으면 지표 모수에서 빠진다 — 0 으로 세지 않는다.
  /**
   * 첫 상담원 응대 시각(serverTimestamp). 아래 둘 중 **먼저 일어난 쪽**에 한 번만 찍힌다:
   *   ① 상담원이 답변을 전송(messages role='agent' 첫 건)
   *   ② 상담원이 상태를 '답변완료' 또는 '통화 후 처리완료'로 전환(전화·현장 응대 후 수기 종결 — 실측상
   *      답변완료 18건 중 4건이 메시지 없이 상태만 바뀌어 있었다. 메시지만 세면 그 4건은 영영 지표 밖이다).
   *   'closed' 전환은 찍지 않는다 — 스팸·중복 종결은 응대가 아니다.
   */
  firstReplyAt?: TimestampLike;
  /**
   * 첫 응대를 **실제로 한** 상담원 이메일. `assignedTo` 와 다를 수 있고, 담당자별 집계는 이쪽을 쓴다
   * (배정은 나중에 바뀌지만 '누가 그때 응대했는가'는 바뀌지 않는다).
   */
  firstReplyBy?: string;
  /**
   * 미회신 지연 리마인드를 **마지막으로** 보낸 시각(`replyReminder` onSchedule).
   * 다음 발송은 여기서 `REPLY_REMINDER_REPEAT_MS` 만큼 지난 뒤(업무시간 중)에 나간다.
   *
   * 🔴 2026-09-01 이전엔 "있으면 영구 skip"(1회성 플래그)이었다. 반복 독촉으로 바뀌면서
   *    의미가 '보낸 적 있음' → '마지막 발송 시각'으로 옮겨졌다. 그때 남은 문서엔
   *    `replyReminderCount` 가 없으므로 **1회 보낸 것으로 해석**한다(아래 필드 주석).
   */
  replyReminderSentAt?: TimestampLike;
  /**
   * 지금까지 보낸 독촉 횟수(1차 포함). `REPLY_REMINDER_MAX` 에 닿으면 개인 독촉을 멈춘다.
   * 🔴 없는데 `replyReminderSentAt` 만 있으면 **1**로 친다 — 옛 1회성 플래그 시대의 문서다.
   *    0 으로 치면 그 건들에 1차가 한 번 더 나간다.
   */
  replyReminderCount?: number;

  // ── 재문의(같은 고객이 이어서 다시 문의) ────────────────────
  //   판정 기준·임계값의 실측 근거는 `followup.ts` 헤더. 둘 다 **접수 시점에 한 번만** 쓰인다
  //   (나중에 update 로 채우면 tickets 의 알림 트리거가 통째로 깨어난다 — tasks-log.md 2026-09-02).
  /**
   * 전화번호 정규화 키(숫자만·+82→0). 재문의 조회의 조인 키.
   * 🔴 2026-09-09 이전 티켓엔 **없다**(백필 미실행) — 그 시절 건끼리는 `customerPhone` 원문
   *    일치로만 만난다. 화면 조회는 두 경로를 모두 훑어 합친다(`useCustomerHistory`).
   */
  customerPhoneKey?: string;
  /**
   * 접수 시점에 찾아낸 **직전 티켓 표식**. 없으면 첫 문의이거나 기간 밖이다.
   * 목록 칩이 이 값만 읽으므로 티켓 N건에 추가 read 가 0 이다.
   */
  followUp?: {
    kind: 'followup' | 'duplicate';
    priorTicketId: string;
    priorCreatedAt?: TimestampLike;
    /** 이 고객의 이전 티켓 총 건수(직전 1건이 아니라 전부). */
    priorCount: number;
  };

  // ── 「이어서 문의하기」(②번 · 고객이 같은 티켓에 이어 쓴 것) ──────
  /**
   * 링크 토큰. **첫 회신 때** `notifyReply` 의 선점 트랜잭션 안에서 발급되고 그대로 유지된다
   * (재발급하면 이미 보낸 링크가 죽는다). 접수 때 만들지 않는 이유 = 어차피 일어나는 write 에
   * 얹어 **트리거를 한 번 더 깨우지 않기** 위해서. 화면에는 노출하지 않는다.
   */
  followUpToken?: string;
  /** 고객이 이어 붙인 횟수(남용 방지 상한 판정용). */
  followUpAppendCount?: number;
  /**
   * 고객이 **마지막으로 말한** 시각.
   * 🔴 이 값이 `firstReplyAt` 보다 뒤면 그 티켓은 **다시 미회신**이다 — 독촉(`replyReminder`)과
   *    화면이 이 비교로 판정한다. `firstReplyAt` 을 지워서 되돌리는 방식은 쓰지 않는다:
   *    «그때 누가 얼마 만에 응대했나»는 지나간 사실이라 지우면 회신속도 지표가 소급해 거짓말한다.
   */
  lastCustomerMessageAt?: TimestampLike;

  // inquiry 전용
  category?: InquiryCategory;

  // lost_item 전용
  /** 분실물품. */
  itemName?: string;
  lostDate?: string;
  /**
   * 분실 **시간대**(`LOST_TIME_SLOTS` 의 값이 그대로 박힌다 — 예 '18:00~19:00').
   * 🔴 날짜만으로는 어느 차량인지가 안 나온다 — 같은 노선도 하루 여러 회차라 상담원이
   *    «몇 시 차였나»를 되묻는 한 바퀴가 매번 생겼다(2026-09-10 way · 전 거래처).
   *    분 단위를 안 묻는 이유는 고객이 정확히 기억하지 못해서다(그래서 '시간대').
   */
  lostTime?: string;
  /**
   * 출발 시간(2026-10-13 YCAmtzH2) — 거래처 시간표(`tenants.routeSchedule`)에서 고객이 고른 값 그대로
   *   (예 '퇴근 17:45'). 이 값이 있으면 분실 시간대(lostTime)는 묻지 않는다(출발 시간이 더 정확).
   *   🔴 서버는 필수·목록 일치로 거부하지 않는다 — 묵은 iframe 번들은 이 칸 없이 제출한다(lostTime 과 같은 판단).
   */
  departureTime?: string;
  /** 분실 위치. */
  lostLocation?: string;
  itemStatus?: LostItemStatus;
  /** 보관 위치. */
  storageLocation?: string;
  returnMethod?: string;

  createdAt?: TimestampLike;
  updatedAt?: TimestampLike;
}

// ────────────────────────────────────────────────────────────
// tickets/{ticketId}/messages — 대화 스레드 (채널톡 스타일)
// ────────────────────────────────────────────────────────────

export type MessageRole = 'customer' | 'agent' | 'bot';

export interface TicketMessage {
  id: string;
  ticketId: string;
  role: MessageRole;
  text: string;
  /** 상담원 메시지의 작성자 이메일(role='agent'). */
  authorEmail?: string;
  attachments?: StoredFile[];
  createdAt?: TimestampLike;
  /**
   * 보낸 뒤 수정한 시각(상담원 본인 메시지만 · 2026-09-22 강유정 요청).
   * 🔴 이미 나간 알림톡·메일은 **회수되지 않는다** — 바뀌는 건 스레드·포털·이어 문의 화면뿐이다.
   */
  editedAt?: TimestampLike;
  /** 첫 수정 직전의 원문(한 번만 기록 — 고객이 실제로 받은 글이 무엇이었는지 남긴다). */
  originalText?: string;
  /**
   * 삭제 시각. 삭제 = `text` 를 비우고 원문은 `deletedText` 로 옮긴다.
   * 🔴 문서를 지우지 않는 이유: 메시지를 읽는 곳(포털·이어 문의·추천 답변·알림)이 전부
   *    «빈 text 는 건너뛴다» 로 되어 있어 **비우기만 하면 고객 쪽에서 자동으로 사라지고**,
   *    원문은 상담원 화면에 남아 무엇을 지웠는지 추적할 수 있다.
   */
  deletedAt?: TimestampLike;
  deletedText?: string;
}

// ────────────────────────────────────────────────────────────
// tickets/{ticketId}/comments — 사내 코멘트 (상담원 ↔ 영업담당자)
//   🔴 **고객에게 보이지 않는다**. `messages` 는 본인 티켓 고객도 read 할 수 있어(rules) 내부
//      메모를 그 안에 섞으면 고객에게 새어 나간다 — 그래서 별도 서브컬렉션이다.
//   🔴 코멘트는 **고객 응대가 아니다** — 티켓 status·firstReplyAt 을 건드리지 않는다(회신속도
//      지표가 "내부 메모를 썼다"로 채워지면 그 숫자는 더 이상 응대를 뜻하지 않는다).
// ────────────────────────────────────────────────────────────

/** 코멘트 알림 채널 — 구글챗 DM 우선, 미개설·실패면 이메일 폴백. */
export type CommentNotifyChannel = 'chat' | 'email';

/** 발송 결과. `skipped` = 보낼 수 없었음(DM 미개설·이메일 없음 등 — 실패와 구분한다). */
export type CommentNotifyResult = 'sent' | 'skipped' | 'failed';

/** 수신자 구분 — 왜 이 사람이 받았는지가 화면에 남아야 한다. */
export type CommentNotifyRole = 'sales' | 'assignee' | 'participant';

export interface CommentNotifyTarget {
  name?: string;
  email?: string;
  role: CommentNotifyRole;
  /** 실제로 나간 채널(둘 다 실패면 마지막 시도 채널). */
  channel?: CommentNotifyChannel;
  result: CommentNotifyResult;
}

export interface TicketComment {
  id: string;
  ticketId: string;
  text: string;
  authorEmail: string;
  authorName?: string;
  /** 작성 시 '담당자에게 알림' 체크 여부. false 면 **기록만** 하고 발송하지 않는다. */
  notify: boolean;
  /**
   * 알림 처리 완료 시각(멱등 플래그). 있으면 트리거가 다시 보내지 않는다 —
   * 이 필드 update 는 `onDocumentCreated` 를 재발화시키지 않지만, 재시도(트리거 자체의
   * at-least-once 배달)에는 이 플래그만이 유일한 방어다.
   */
  notifiedAt?: TimestampLike;
  /** 발송 결과 denorm — "누구에게 갔는지"는 코멘트 옆에 보여야 한다(조용한 미도달 방지). */
  notifyTargets?: CommentNotifyTarget[];
  /**
   * 영업팀+기획팀 스페이스 스레드 답글 결과(2026-09-17~). `skipped` = 웹훅 미설정.
   * 이전 코멘트엔 없다(undefined = 그때는 스페이스에 안 올렸다).
   */
  spaceResult?: CommentNotifyResult;
  createdAt?: TimestampLike;
  /**
   * 작성 뒤 수정한 시각(본인 또는 super · 2026-09-29 게시판 eQb1Qcoy — 상담원 답변 수정과 같은 방식).
   * 🔴 이미 나간 구글챗 DM·스페이스 답글·메일은 **회수되지 않는다** — 바뀌는 건 콘솔 스레드뿐이다.
   */
  editedAt?: TimestampLike;
  /** 첫 수정 직전의 원문(한 번만 기록 — 알림으로 실제 나간 글이 무엇이었는지 남긴다). */
  originalText?: string;
  /**
   * 삭제 시각. 삭제 = `text` 를 비우고 원문은 `deletedText` 로 옮긴다(문서는 지우지 않는다 —
   * `notifyTargets` 발송 기록은 사실이므로 남긴다). 삭제된 코멘트는 다시 고칠 수 없다.
   */
  deletedAt?: TimestampLike;
  deletedText?: string;
}

export const COMMENT_NOTIFY_ROLE_LABELS: Record<CommentNotifyRole, string> = {
  sales: '영업담당',
  assignee: '담당 상담원',
  participant: '코멘트 참여자',
};

// ────────────────────────────────────────────────────────────
// faq_entries — 거래처별 FAQ
// ────────────────────────────────────────────────────────────

/**
 * FAQ 에 딸린 외부 링크(정류장 지도 등).
 *
 * ⚠️ **본문(answer)이 아니라 별도 필드인 이유**: 챗봇 응답은 Gemini 가 FAQ 답변을
 *    "고객 질문 맥락에 맞게 풀어써서"(systemInstruction 규칙 2) 만든다. 본문에 URL 을
 *    적어 두면 요약·변형 과정에서 잘리거나 글자가 바뀌어 **깨진 링크**가 나갈 수 있다.
 *    링크는 csBotCore 가 Gemini 응답 **뒤에 원문 그대로 덧붙인다**(무손실 보장).
 */
export interface FaqLink {
  /** 표시 라벨(정류장명 등). */
  label: string;
  /** http(s) URL. 지도 링크는 보통 `https://map.kakao.com/link/map/<이름>,<위도>,<경도>`. */
  url: string;
}

export interface FaqEntry {
  id: string;
  tenantId: string;
  category: string;
  question: string;
  answer: string;
  keywords: string[];
  active: boolean;
  hitCount?: number;
  /** 정류장 지도 링크 등(노선 엑셀 업로드가 생성). 콘솔 FAQ 편집은 이 필드를 건드리지 않는다. */
  links?: FaqLink[];
  createdAt?: TimestampLike;
  updatedAt?: TimestampLike;
}

// ────────────────────────────────────────────────────────────
// unmatched_questions — 챗봇 무매치 적재
// ────────────────────────────────────────────────────────────

export interface UnmatchedQuestion {
  id: string;
  tenantId: string;
  text: string;
  ticketId?: string;
  category?: string;
  reviewed: boolean;
  createdAt?: TimestampLike;
}

// ────────────────────────────────────────────────────────────
// agents — 상담원
// ────────────────────────────────────────────────────────────

export type AgentRole = 'super' | 'agent';

export interface Agent {
  /** docId 와 동일(이메일). */
  email: string;
  role: AgentRole;
  /** 담당 거래처(빈 배열=전체). */
  assignedTenants: string[];
  displayName: string;
  active: boolean;
  /**
   * 휴대폰(숫자만 · 2026-09-18). 미회신 독촉 알림톡(`dycsremind`) 수신용 — 비면 알림톡만 빠지고
   * 이메일·챗 DM 은 그대로 간다. 배정된 건의 1~3차 독촉은 담당 상담원 단독 수신이라 여기가 비면 폰이 안 울린다.
   */
  phone?: string;
  createdAt?: TimestampLike;
  updatedAt?: TimestampLike;
}

// ────────────────────────────────────────────────────────────
// clients/{email} — 거래처(고객사) 열람 계정  · 2026-09-01
//
// 🔴 **`agents` 와 절대 합치지 않는다.** 상담원 명부에 고객사를 넣으면 rules 의 `isAgent()` 게이트가
//    한꺼번에 열리고 그중 하나가 `tickets/{id}/comments`(사내 코멘트)다 — 내부 지시가 고객사에
//    그대로 보인다. 권한을 **깎는** 구조는 새 기능이 생길 때마다 빠뜨릴 수 있으므로,
//    닫힌 상태에서 필요한 것만 **더하는** 별도 컬렉션으로 둔다.
// 🔴 **도메인 게이트가 아니라 명부 게이트**다. `@yuhs.ac` 를 허용 도메인에 넣으면 연세의료원
//    **전 직원**에게 문이 열린다 — 봐야 할 사람은 총무팀 두세 명이다. 여기 등록된 사람만 들어온다.
// 🔴 **읽기 전용**. 답변·상태 전이·배정은 전부 상담원 몫이라 rules 어디에도 client write 가 없다.
// ────────────────────────────────────────────────────────────

export interface ClientUser {
  /** docId 와 동일(로그인 이메일). 🔴 Auth 이메일과 글자 단위로 같아야 한다(agents 와 같은 함정). */
  email: string;
  /**
   * 볼 수 있는 거래처 **한 곳**. `agents.assignedTenants` 와 달리 배열이 아니고 빈 값도 없다 —
   * "빈 배열 = 전체"라는 스키마를 여기에 들이면 **실수로 비운 계정이 전 거래처를 보게 된다**.
   */
  tenantId: string;
  displayName: string;
  /** 소속·직함(예: '총무팀'). 화면 표시용 — 없으면 안 그린다. */
  affiliation?: string;
  /** 끄면 즉시 차단(퇴사·인사이동). 삭제 대신 이 값으로 처분한다 — agents 와 같은 관례. */
  active: boolean;
  /** 초대 메일을 마지막으로 보낸 시각(재발송 이력). */
  invitedAt?: TimestampLike;
  /** 첫 로그인 시각 — 비어 있으면 "초대만 하고 아직 안 들어옴"이 화면에 보인다. */
  firstSignedInAt?: TimestampLike;
  lastSignedInAt?: TimestampLike;
  createdAt?: TimestampLike;
  updatedAt?: TimestampLike;
}

// ────────────────────────────────────────────────────────────
// improvement_requests — 개선 요청 게시판 (DYOPS 이식)
// ────────────────────────────────────────────────────────────

export type ImprovementRequestStatus =
  | 'requested'
  | 'reviewing'
  | 'in_progress'
  | 'done'
  | 'rejected';

export const IMPROVEMENT_STATUS_LABELS: Record<ImprovementRequestStatus, string> = {
  requested: '요청',
  reviewing: '검토',
  in_progress: '반영중',
  done: '완료',
  rejected: '반려',
};

/** 상태 → Badge tone(@dycs/ui BadgeTone). */
export const IMPROVEMENT_STATUS_TONE: Record<
  ImprovementRequestStatus,
  'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info'
> = {
  requested: 'info',
  reviewing: 'warning',
  in_progress: 'accent',
  done: 'success',
  rejected: 'danger',
};

/** 열람 범위 — 신규 문서는 항상 명시 저장. 미설정(레거시)='all'. */
export type ImprovementRequestVisibility = 'all' | 'private';

/**
 * 개선 요청 대상 메뉴 — 콘솔 화면 + 고객 접점 기준. 작성 폼 select 옵션.
 * 마지막 '기타'는 미분류용(필수).
 */
export const DYCS_MENU_OPTIONS = [
  '인박스(티켓)',
  'FAQ 관리',
  '노선 안내',
  '통계',
  '알림 설정',
  '거래처 관리',
  '고객 위젯',
  '카카오 채널',
  '기타',
] as const;
export type DycsMenu = (typeof DYCS_MENU_OPTIONS)[number];

/** 참조 링크(표시명 + URL). */
export interface RefLink {
  title: string;
  url: string;
}

/** 처리 이력 1건 — 상태 전이(statusTo 有) 또는 코멘트(statusTo 無). arrayUnion append. */
export interface ImprovementHistoryEntry {
  /** 상태 전이일 때만 — 없으면 단순 코멘트. */
  statusTo?: ImprovementRequestStatus;
  byEmail: string;
  byName: string;
  /** 배열 요소라 serverTimestamp 불가 → Timestamp.now()(StoredFile 동일 관례). */
  at: TimestampLike;
  comment?: string;
}

export interface ImprovementRequest {
  /** Firestore doc id(본 객체에 주입). */
  id: string;
  title: string;
  content: string;
  requesterEmail: string;
  requesterName: string;
  status: ImprovementRequestStatus;
  /** 열람 범위 — 신규는 항상 명시('all'|'private'). 미설정(레거시)='all'. */
  visibility?: ImprovementRequestVisibility;
  /** 대상 프로젝트(기본 'DYCS'). 다른 시스템 개선 요청 시 자유 입력. */
  project?: string;
  /** 대상 메뉴(DYCS_MENU_OPTIONS 중 하나 또는 미선택). */
  menu?: string;
  /** 참조 링크(여러 개). `normalizeRefLinks` 로 정규화 저장. */
  refLinks?: RefLink[];
  /** 첨부(이미지·문서 등 전 형식). 필드명은 screenshots 유지(의미는 범용 첨부). */
  screenshots: StoredFile[];
  /** 처리 이력 타임라인(arrayUnion append). */
  history: ImprovementHistoryEntry[];
  /** 완료 처리 결과(텍스트). */
  resultNote?: string;
  /** 완료 처리 참고 이미지(붙여넣기 첨부·상태 전이와 독립). */
  resultImages?: StoredFile[];
  createdAt?: TimestampLike;
  updatedAt?: TimestampLike;
}

// ────────────────────────────────────────────────────────────
// replyLogs — 회신 발송 감사
// ────────────────────────────────────────────────────────────

/**
 * 🔴 `'accepted'` 는 `'sent'` 의 동의어가 아니다 — **알림톡 전용**이고 "SENS 가 요청을 받았다"
 *   (HTTP 202)까지만 뜻한다. 수신자가 카카오 메시지를 못 받는 상태(`3019` 등)면 요청은 202 인 채
 *   도달만 실패하고, 그 사실은 NCP 콘솔 «수신 상태 코드»에만 남는다. 2026-09-02 이전 로그는
 *   이 구분이 없어 **미도달 건도 `'sent'` 로 적혀 있다**(과거 데이터 해석 시 주의).
 *   → 알림톡을 «보냈다»고 단정하는 화면·집계를 이 값 위에 만들지 말 것.
 */
/** 감사 로그의 채널 — 회신 채널 2종 + **자체 SMS 폴백**(알림톡 미도달 확정 시). `tenants.replyChannels` 엔 넣지 않는다: 설정 채널이 아니라 안전망이다. */
export type ReplyLogChannel = ReplyChannel | 'sms';

export type ReplyLogResult = 'sent' | 'accepted' | 'failed';

export interface ReplyLog {
  id: string;
  tenantId: string;
  ticketId: string;
  channel: ReplyLogChannel;
  /** 수신처(휴대폰 또는 이메일). */
  to: string;
  result: ReplyLogResult;
  /** 실패 시 사유. */
  error?: string;
  /** 알림톡 전용 — SMS 대체발송을 **NCP 가 받아들였는지**(붙여 보낸 것과 다르다 — 설정이 없으면 202 로 받고 조용히 버린다). */
  smsFailover?: boolean;
  /** 🔴 알림톡 전용 — 사후 도달 조회의 유일한 열쇠(`GET …/messages/{messageId}` 로 3019 등 수신 상태 확인). 2026-09-02 이전 로그엔 없다. */
  messageId?: string;
  requestId?: string;
  createdAt?: TimestampLike;
}

// ────────────────────────────────────────────────────────────
// alertLogs — 사내 알림 발송 감사 (실시간 알림톡/이메일 + 다이제스트)
// ────────────────────────────────────────────────────────────

export type StaffAlertChannel = 'alimtalk' | 'email' | 'chat';

export interface AlertLog {
  id: string;
  tenantId: string;
  /** 실시간 알림은 단일 티켓, 챗 다이제스트는 복수라 optional. */
  ticketId?: string;
  channel: StaffAlertChannel;
  /** 수신처(휴대폰 / 이메일 / 'chat-space'). */
  to: string;
  /** 수신자 역할(실시간 알림). */
  role?: StaffAlertRole;
  result: ReplyLogResult;
  error?: string;
  createdAt?: TimestampLike;
}

// ────────────────────────────────────────────────────────────
// phone_verifications — 휴대폰 인증번호 (홈페이지 창구 남용 방지)
//   🔴 클라는 이 컬렉션을 **읽지도 쓰지도 못한다**(rules 전면 차단·Function 전용).
//      코드가 클라에 보이면 인증 자체가 무의미하고, 조회가 열리면 번호 목록이 새어 나간다.
//   저장하는 건 코드 원문이 아니라 **해시**다 — 콘솔·백업·유출 어디에서도 코드를 못 읽는다.
// ────────────────────────────────────────────────────────────

export interface PhoneVerification {
  /** docId(요청 식별자) — 위젯이 보관했다가 확인 단계에 되돌려준다. */
  id: string;
  tenantId: string;
  /** 숫자만 정규화한 수신 번호. */
  phone: string;
  /** 인증번호 해시(sha256(code + salt)). 원문은 저장하지 않는다. */
  codeHash: string;
  salt: string;
  /** 발송 채널(알림톡 우선·실패 시 SMS 폴백). */
  channel: 'alimtalk' | 'sms';
  /** 시도 횟수(무차별 대입 차단 — 상한 초과 시 폐기). */
  attempts: number;
  /** 인증 성공 시각. */
  verifiedAt?: TimestampLike;
  /**
   * 인증 성공 후 발급되는 1회용 토큰. 접수(escalate)가 이 토큰으로 "이 번호는 인증됐다"를 증명한다.
   * 티켓이 만들어지면 즉시 소모(consumedAt) — 한 번 인증으로 여러 건을 찍어내지 못한다.
   */
  verifyToken?: string;
  consumedAt?: TimestampLike;
  /** 만료 시각(발송 후 3분 · 인증 후 토큰은 10분). */
  expiresAt: TimestampLike;
  createdAt?: TimestampLike;
}

// ────────────────────────────────────────────────────────────
// 라벨 맵 (한국어 UI)
// ────────────────────────────────────────────────────────────

export const STAFF_ALERT_ROLE_LABELS: Record<StaffAlertRole, string> = {
  cs: 'CS 담당자',
  account: '거래처 담당자',
  lead: '팀장',
};

export const TICKET_TYPE_LABELS: Record<TicketType, string> = {
  inquiry: '문의',
  lost_item: '분실물',
};

export const TICKET_STATUS_LABELS: Record<TicketStatus, string> = {
  received: '접수',
  in_progress: '처리중',
  answered: '답변완료',
  phone_resolved: '통화 후 처리완료',
  closed: '종료',
};

export const LOST_ITEM_STATUS_LABELS: Record<LostItemStatus, string> = {
  storing: '보관중',
  returned: '인계완료',
  not_found: '미발견',
};

/**
 * 분실 시간대 선택지(접수 폼 드롭다운 · 티켓엔 이 문자열이 **그대로** 박힌다).
 *   - 저장값 = 표시값이라 인박스·챗 카드·포털이 **매핑 없이** 같은 글자를 쓴다(라벨 표를 따로
 *     두면 한쪽만 고쳐져 같은 티켓이 화면마다 다른 시간으로 보인다).
 *   - 셔틀은 이른 새벽부터 늦은 밤까지 돌아 양 끝을 **열린 구간**으로 받는다.
 *   - **'기억나지 않음'을 남겨 둔다** — 시간을 모르는 사람의 접수를 막는 건 되묻는 것보다 나쁘다.
 *     빈칸과 달리 «물어봤고 고객도 모른다»는 사실이 남아 상담원이 같은 것을 다시 묻지 않는다.
 */
export const LOST_TIME_SLOTS: string[] = [
  '05:00 이전',
  ...Array.from({ length: 18 }, (_, i) => {
    const h = 5 + i;
    return `${String(h).padStart(2, '0')}:00~${String(h + 1).padStart(2, '0')}:00`;
  }),
  '23:00 이후',
  '기억나지 않음',
];

export const INQUIRY_CATEGORIES: InquiryCategory[] = [
  '노선',
  '시간',
  '서비스',
  '칭찬',
  '기타',
];
